import { useState, useEffect } from 'react';

/**
 * A custom hook that keeps a state variable synchronized with localStorage.
 * 
 * @param {string} key - The key to use in localStorage.
 * @param {any} initialValue - The initial value to use if no value is found in localStorage.
 * @returns {[any, Function]} - The current value and a function to update it.
 */
function useLocalStorage(key, initialValue) {
    // Use a function for the initial state to avoid reading from localStorage on every render
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    });

    // Update localStorage whenever the state changes
    useEffect(() => {
        try {
            window.localStorage.setItem(key, JSON.stringify(storedValue));
        } catch (error) {
            console.warn(`Error setting localStorage key "${key}":`, error);
        }
    }, [key, storedValue]);

    return [storedValue, setStoredValue];
}

export default useLocalStorage;
